import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { resolveReadableHttpErrorMessage } from "../../../lib/http/errors";
import type { BusVehicleTypeFieldErrors, BusVehicleTypeItem, BusVehicleTypeMutation } from "../types";
import { busVehicleTypeDefaults, busVehicleTypeQueryKey, emptyBusVehicleType } from "../config";
import { createBusVehicleType, fetchBusVehicleTypes, normalizeBusVehicleType, updateBusVehicleType, validateBusVehicleType } from "../service/busVehicleTypeService";

/** 管理车型列表和可保留输入的编辑会话。 */
export function useBusVehicleTypeRuntime(canManage: boolean) {
  const client = useQueryClient();
  const [editor, setEditor] = useState<{ id?: string } | null>(null);
  const [draft, setDraft] = useState<BusVehicleTypeMutation>(emptyBusVehicleType);
  const [draftFilters, setDraftFilters] = useState<{ keyword: string; status: string }>({ keyword: "", status: "" });
  const [params, setParams] = useState(busVehicleTypeDefaults);
  const [fieldErrors, setFieldErrors] = useState<BusVehicleTypeFieldErrors>({});
  const [errorMessage, setErrorMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const saveLock = useRef(false);
  const query = useQuery({ queryKey: [...busVehicleTypeQueryKey, "list", params],
    queryFn: () => fetchBusVehicleTypes(params), refetchOnWindowFocus: false });
  const mutation = useMutation({
    mutationFn: ({ id, payload }: { id?: string; payload: BusVehicleTypeMutation }) =>
      id ? updateBusVehicleType(id, payload) : createBusVehicleType(payload),
    onSuccess: () => client.invalidateQueries({ queryKey: busVehicleTypeQueryKey }),
  });

  return { editor, draft, setDraft, fieldErrors, errorMessage, saving, params, draftFilters, setDraftFilters,
    items: query.data?.items ?? [], total: query.data?.total ?? 0, loading: query.isFetching,
    queryError: query.error ? "车型列表加载失败，请重试。" : "",
    refresh() { void query.refetch(); },
    search() { setParams({ ...params, page: 1, keyword: draftFilters.keyword.normalize("NFKC").trim() || undefined,
      isDisabled: draftFilters.status === "" ? undefined : draftFilters.status === "disabled" }); },
    reset() { setDraftFilters({ keyword: "", status: "" }); setParams(busVehicleTypeDefaults); },
    setPage(page: number, pageSize: number) { setParams({ ...params, page: pageSize === params.pageSize ? page : 1, pageSize }); },
    setSort(sortField: typeof params.sortField, sortDirection: typeof params.sortDirection) {
      setParams({ ...params, page: 1, sortField, sortDirection });
    },
    openEditor(item?: BusVehicleTypeItem) {
      if (!canManage || saveLock.current) return;
      setEditor({ id: item?.id });
      setDraft(item ? { name: item.name, seatCount: item.seatCount, sortNo: item.sortNo, isDisabled: item.isDisabled } : { ...emptyBusVehicleType });
      setFieldErrors({}); setErrorMessage("");
    },
    closeEditor() { if (!saveLock.current) setEditor(null); },
    async save() {
      if (saveLock.current || !editor) return false;
      if (!canManage) { setErrorMessage("维护权限已变更，请重新确认权限。"); return false; }
      const errors = validateBusVehicleType(draft);
      setFieldErrors(errors); setErrorMessage("");
      if (Object.keys(errors).length) return false;
      saveLock.current = true; setSaving(true);
      try {
        await mutation.mutateAsync({ id: editor.id, payload: normalizeBusVehicleType(draft) });
        setEditor(null);
        return true;
      } catch (error) {
        setErrorMessage(resolveReadableHttpErrorMessage(error, "保存失败，请检查后重试。"));
        return false;
      } finally {
        saveLock.current = false; setSaving(false);
      }
    },
  };
}
